/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AmplitudeService } from '@inyova/inyova-shared';
import { FeatureFlags } from '@inyova/models';
import { isCustomer, isInvestAccount } from '@inyova/utils';
import { Storage } from '@ionic/storage-angular';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash-es';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { EMPTY, from, of } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import * as AccountActions from '@account/account.actions';
import * as GrowActions from '@grow/inyova-grow.actions';
import * as HomeActions from '@home/home.actions';
import * as fromAccount from '@account/account.reducers';
import * as fromRoot from '@app/app.reducers';
import * as fromHome from '@home/reducers';

import { IonicStorageKeys } from '@app/app.constants';
import {
  isAnnualPerformanceRange,
  normalizePerformanceRange,
  performanceEventsMap
} from '@home/components/home-performance-chart/home-performance-chart.util';
import { ImpactStoryDetail, PerformanceDataFilter, PerformanceFilterRange, PerformanceType, PromoCampaign } from '@shared/models/Home';
import { ToastService } from '@shared/services/toast.service';
import { YovaAPI } from '@shared/yova-api';

@Injectable()
export class HomeEffects {
  private readonly defaultPerformanceFilters: PerformanceDataFilter = {
    range: PerformanceFilterRange.LAST_PORTFOLIO_UPDATE,
    chartType: PerformanceType.PERFORMANCE,
    showPortfolioChanges: true,
    showBenchmark: false,
    lastExtendedRange: PerformanceFilterRange.LAST_PORTFOLIO_UPDATE
  };

  getHomeData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HomeActions.getHomeData),
        concatLatestFrom(() => this.store.select(fromAccount.selectCustomer)),
        tap(([, customer]) => {
          // Initialise main performance data
          if (customer.accounts && customer.accounts.length) {
            this.store.dispatch(HomeActions.ensurePerformanceAccountData());
          }
          if (customer.has_interest_account) {
            // Initialise Grow acc data
            this.store.dispatch(GrowActions.getInyovaGrowAccount());
          }
          if (customer.has_crowd_investor_account) {
            // Initialise crowdinvestor data
            this.store.dispatch(HomeActions.getCrowdInvestingData());
          }
          this.store.dispatch(HomeActions.getAllReportingsStatus());
        })
      ),
    { dispatch: false }
  );

  ensurePerformanceAccountData$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HomeActions.ensurePerformanceAccountData),
        concatLatestFrom(() => [
          this.store.select(fromAccount.selectCustomer),
          this.store.select(fromHome.selectAccountsGraphData),
          this.store.select(fromHome.selectAccountsEndValues)
        ]),
        tap(([, customer, accountsGraphData, accountsEndValues]) => {
          customer.accounts?.forEach((account) => {
            if (isCustomer(account)) {
              if (!accountsEndValues.some((item) => item.account.id === account.id)) {
                this.store.dispatch(HomeActions.getEndValues({ account }));
              }

              if (!accountsGraphData.some((item) => item.account.id === account.id)) {
                this.store.dispatch(HomeActions.getGraphData({ account }));
              }
            }
          });
        })
      ),
    { dispatch: false }
  );

  getGraphData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.getGraphData),
      mergeMap(({ account }) =>
        this.yovaAPI.dashboard.getPerformance(account.id, PerformanceFilterRange.ALL).pipe(
          map((res) => HomeActions.getGraphDataSuccess({ account, graphData: res.performance })),
          catchError(() => {
            return of(HomeActions.getGraphDataFail({ account, error: true }));
          })
        )
      )
    )
  );

  loadSavedPerformanceFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.loadPerformanceFilters),
      concatLatestFrom(() => [
        this.store.select(fromRoot.selectSelectedAccount),
        this.store.select(fromAccount.selectAccountFeatureFlag(FeatureFlags.PORTFOLIO_UPDATES))
      ]),
      filter(([, selectedAccount]) => !!selectedAccount?.id && selectedAccount.kind !== 'grow'),
      concatMap(([, selectedAccount, hasPortfolioUpdatesFlag]) =>
        from(this.storage.get(this.getPerformanceFiltersStorageKey(selectedAccount.id))).pipe(
          map((savedFilter: PerformanceDataFilter | null) => ({
            savedFilter: savedFilter ?? {
              ...this.defaultPerformanceFilters,
              ...(!hasPortfolioUpdatesFlag && {
                range: PerformanceFilterRange.ALL,
                lastExtendedRange: PerformanceFilterRange.ALL,
                showPortfolioChanges: false
              })
            },
            accountId: selectedAccount?.id
          }))
        )
      ),
      map(({ accountId, savedFilter }) => {
        return HomeActions.restoreSavedPerformanceFilter(accountId, savedFilter);
      })
    )
  );

  restoreSavedPerformanceFilter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.restoreSavedPerformanceFilter),
      switchMap(({ accountId, savedFilter }) =>
        this.store.select(fromHome.selectAccountsGraphDataCurrentAccount).pipe(
          concatLatestFrom(() => this.store.select(fromHome.selectAvailableAnnualFilters)),
          filter(([accountGraphData]) => accountGraphData?.account?.id === accountId),
          filter(([accountGraphData]) => accountGraphData?.initialised),
          take(1),
          map(([, availableAnnualFilters]) => {
            const isRangeYear = isAnnualPerformanceRange(savedFilter.range);
            const isLastExtendedRangeYear = isAnnualPerformanceRange(savedFilter.lastExtendedRange);

            let normalizedFilter = {
              ...savedFilter
            };

            if (isRangeYear) {
              normalizedFilter = {
                ...normalizedFilter,
                range: normalizePerformanceRange(savedFilter.range, availableAnnualFilters)
              };
            }

            if (isLastExtendedRangeYear) {
              normalizedFilter = {
                ...normalizedFilter,
                lastExtendedRange: normalizePerformanceRange(savedFilter.lastExtendedRange, availableAnnualFilters)
              };
            }

            return HomeActions.hydratePerformanceFilter(
              normalizedFilter.range,
              normalizedFilter.chartType,
              normalizedFilter.showPortfolioChanges,
              normalizedFilter.showBenchmark,
              normalizedFilter.lastExtendedRange
            );
          })
        )
      )
    )
  );

  loadHydratedPerformanceGraph$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.hydratePerformanceFilter),
      map(({ range }) => HomeActions.getPerformanceGraph(range))
    )
  );

  savePerformanceAllFilter$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HomeActions.getPerformanceGraph),
        filter(({ range }) => range === PerformanceFilterRange.ALL),
        concatLatestFrom(() => [this.store.select(fromHome.selectPerformanceFilters), this.store.select(fromRoot.selectSelectedAccount)]),
        filter(([, , account]) => !!account?.id && account.kind !== 'grow'),
        tap(([{ range }, filter, account]) => {
          if (account.kind === '3a' || account.kind === '3b') {
            this.amplitudeService.updateUserProperties([
              {
                propName: `time_horizon_selection_${account.kind}`,
                propValue: `${performanceEventsMap[range] ?? 'calendar_year'}`
              }
            ]);
          }

          void this.storage.set(this.getPerformanceFiltersStorageKey(account.id), filter);
        })
      ),
    { dispatch: false }
  );

  getPerformanceData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.getPerformanceGraph),
      filter(({ range }) => range !== PerformanceFilterRange.ALL),
      concatLatestFrom(() => this.store.select(fromRoot.selectSelectedAccount)),
      switchMap(([{ range }, account]) => {
        return this.yovaAPI.dashboard.getPerformance(account.id, range).pipe(
          map(({ performance }) => {
            return HomeActions.getPerformanceGraphSuccess({ graphData: performance, range });
          }),
          catchError(() => of(HomeActions.getPerformanceGraphFailed()))
        );
      })
    )
  );

  savePerformanceRangeFilterOnSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HomeActions.getPerformanceGraphSuccess),
        concatLatestFrom(() => [this.store.select(fromHome.selectPerformanceFilters), this.store.select(fromRoot.selectSelectedAccount)]),
        filter(([, , account]) => !!account?.id && account.kind !== 'grow'),
        tap(([{ range }, filter, account]) => {
          if (account.kind === '3a' || account.kind === '3b') {
            this.amplitudeService.updateUserProperties([
              {
                propName: `time_horizon_selection_${account.kind}`,
                propValue: `${performanceEventsMap[range] ?? 'calendar_year'}`
              }
            ]);
          }
          void this.storage.set(this.getPerformanceFiltersStorageKey(account.id), filter);
        })
      ),
    { dispatch: false }
  );

  savePerformanceFiltersOnToggle$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HomeActions.togglePerformanceGraphType, HomeActions.togglePerformanceEvents, HomeActions.togglePerformanceBenchmarkType),
        concatLatestFrom(() => [this.store.select(fromHome.selectPerformanceFilters), this.store.select(fromRoot.selectSelectedAccount)]),
        filter(([, , account]) => !!account?.id && account.kind !== 'grow'),
        tap(([, filters, account]) => {
          void this.storage.set(this.getPerformanceFiltersStorageKey(account.id), filters);
        })
      ),
    { dispatch: false }
  );

  getEndValues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.getEndValues),
      mergeMap(({ account }) =>
        this.yovaAPI.dashboard.getEndValues(account.id).pipe(
          map((res) => HomeActions.getEndValuesSuccess({ account, data: res.customer.end_values })),
          catchError(() => EMPTY)
        )
      )
    )
  );

  getAllReportingsStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.getAllReportingsStatus),
      mergeMap(() =>
        this.yovaAPI.dashboard.getAllReportingsStatus().pipe(
          map((res) => HomeActions.getAllReportingsStatusSuccess({ data: res.data })),
          catchError(() => EMPTY)
        )
      )
    )
  );

  getImpactStoryDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.getImpactStoryDetail),
      mergeMap(({ id }) =>
        this.yovaAPI.dashboard.getImpactStoryDetail(id).pipe(
          map((res) => HomeActions.getImpactStoryDetailSuccess({ data: res.data })),
          catchError(() => {
            void this.toastService.error(this.translateService.instant('SHARED.errors.something'));
            void this.router.navigateByUrl('/tabs/home');
            return EMPTY;
          })
        )
      )
    )
  );

  getImpactStories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.getImpactStories),
      mergeMap(() =>
        this.yovaAPI.dashboard.getImpactStories().pipe(
          map((res) => HomeActions.getImpactStoriesSuccess({ data: res.data })),
          catchError(() => {
            void this.toastService.error(this.translateService.instant('SHARED.errors.something'));
            return EMPTY;
          })
        )
      )
    )
  );

  dismissMarketUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.dismissMarketUpdate),
      switchMap(({ id }: { id: number }) =>
        this.yovaAPI.dashboard.dismissMarketUpdate().pipe(
          map(() => HomeActions.dismissMarketUpdateSuccess({ id })),
          catchError(() => of(HomeActions.dismissMarketUpdateFail()))
        )
      )
    )
  );

  getImpactMetrics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.getImpactMetrics),
      concatLatestFrom(() => this.store.select(fromAccount.selectCustomerFeatureFlag(FeatureFlags.IMPACT2))),
      filter(([, isImpact2Active]) => !isImpact2Active),
      mergeMap(() =>
        this.yovaAPI.dashboard.getImpactMetrics().pipe(
          map((res) => HomeActions.getImpactMetricsSuccess({ data: res.data })),
          catchError(() => of(HomeActions.getImpactMetricsFail()))
        )
      )
    )
  );

  getCustomerNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.getCustomerNotifications),
      mergeMap(() => this.yovaAPI.notifications.fetchAll().pipe(map((res) => HomeActions.getCustomerNotificationsSuccess({ data: res.data }))))
    )
  );

  getCompaniesStockPerformance$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.getCompaniesStockPerformance),
      mergeMap(({ account }) =>
        this.yovaAPI.dashboard.getCompaniesStockPerformance(account.id).pipe(
          map((res) => {
            return HomeActions.getCompaniesStockPerformanceSuccess({ account, data: res.data });
          }),
          catchError(() => of(HomeActions.getCompaniesStockPerformanceFail({ account })))
        )
      )
    )
  );

  getSuperhumanSurvey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.getSuperhumanSurvey),
      mergeMap(({ nextQuestionID }) =>
        this.yovaAPI.dashboard.getSuperHumanSurvey().pipe(
          map((response) => HomeActions.getSuperhumanSurveySuccess({ survey: response.survey_questions, nextQuestionID })),
          catchError(() => of(HomeActions.getSuperhumanSurveyFailed()))
        )
      )
    )
  );

  getCrowdinvestingData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.getCrowdInvestingData),
      mergeMap(() =>
        this.yovaAPI.dashboard.getCrowdinvestingData().pipe(
          map((response) => {
            return HomeActions.getCrowdInvestingDataSuccess({ data: response.data[0].attributes });
          }),
          catchError(() => EMPTY)
        )
      )
    )
  );

  saveSuperhumanSurveyAnswers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.saveSuperhumanSurveyAnswers),
      concatMap((action) => of(action).pipe(withLatestFrom(this.store.pipe(select(fromAccount.selectCustomer))))),
      mergeMap(([{ submitted, nextQuestionID, answers }, customer]) =>
        this.yovaAPI.dashboard.saveSuperHumanSurvey({ submitted, answers }).pipe(
          map(() => {
            if (!submitted) {
              return HomeActions.getSuperhumanSurvey({ nextQuestionID });
            }
            return HomeActions.markSuperhumanNotificationAsSeen({ customerID: customer.id });
          }),
          catchError(() => {
            void this.toastService.error(this.translateService.instant('SHARED.errors.something'));
            return EMPTY;
          })
        )
      )
    )
  );

  markSuperhumanNotificationAsSeen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.markSuperhumanNotificationAsSeen),
      concatMap((action) => of(action).pipe(withLatestFrom(this.store.pipe(select(fromHome.selectCustomerNotifications))))),
      mergeMap(([, notifications]) => {
        const superhumanNotification = notifications.find((item) => item.attributes.data.type === 'superhuman');
        if (superhumanNotification) {
          return this.yovaAPI.notifications.markAsSeen(superhumanNotification.id).pipe(
            map(() => HomeActions.getCustomerNotifications()),
            catchError(() => EMPTY)
          );
        }
        return EMPTY;
      })
    )
  );

  sendEngagement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.sendEngagement),
      withLatestFrom(this.store.pipe(select(fromHome.selectImpactStoryDetail)), this.store.pipe(select(fromHome.selectImpactStories))),
      mergeMap(([{ action, id, rebalance_portfolio }, article, stories]) =>
        this.yovaAPI.dashboard.sendEngagement(action, id, rebalance_portfolio).pipe(
          mergeMap((response) => {
            const supporters = article.article?.attributes?.supporters_number + 1;
            const updatedStories = cloneDeep(stories);
            updatedStories.forEach((story) => {
              if (Number(story.id) === id) {
                // eslint-disable-next-line no-param-reassign
                story.attributes.liked = action;
                // eslint-disable-next-line no-param-reassign
                story.attributes.supporters_number = supporters;
              }
            });
            return [
              HomeActions.sendEngagementSuccess({ data: response }),
              HomeActions.getImpactStoryDetailSuccess({
                data: {
                  attributes: {
                    ...article.article.attributes,
                    liked: action,
                    supporters_number: supporters
                  }
                } as ImpactStoryDetail
              }),
              HomeActions.getImpactStoriesSuccess({ data: updatedStories })
            ];
          }),
          catchError(() => {
            void this.toastService.error(this.translateService.instant('SHARED.errors.something'));
            return EMPTY;
          })
        )
      )
    )
  );

  deleteEngagement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.deleteEngagement),
      withLatestFrom(this.store.pipe(select(fromHome.selectImpactStoryDetail)), this.store.pipe(select(fromHome.selectImpactStories))),
      mergeMap(([{ id }, article, stories]) =>
        this.yovaAPI.dashboard.deleteEngagement(id).pipe(
          mergeMap(() => {
            const supporters = article.article?.attributes?.supporters_number - 1;
            const updatedStories = cloneDeep(stories);
            updatedStories.forEach((story) => {
              if (Number(story.id) === id) {
                // eslint-disable-next-line no-param-reassign
                story.attributes.liked = false;
                // eslint-disable-next-line no-param-reassign
                story.attributes.supporters_number = supporters;
              }
            });
            return [
              HomeActions.deleteEngagementStatus({ success: true }),
              HomeActions.getImpactStoryDetailSuccess({
                data: {
                  attributes: {
                    ...article.article.attributes,
                    liked: false,
                    supporters_number: supporters
                  }
                } as ImpactStoryDetail
              }),
              HomeActions.getImpactStoriesSuccess({ data: updatedStories })
            ];
          }),
          catchError(() => {
            HomeActions.deleteEngagementStatus({ success: false });
            void this.toastService.error(this.translateService.instant('SHARED.errors.something'));
            return EMPTY;
          })
        )
      )
    )
  );

  getSavingsPlanSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.getSavingsPlanSummary, AccountActions.setCurrentAccount),
      concatMap((action) => of(action).pipe(withLatestFrom(this.store.pipe(select(fromAccount.selectCurrentAccount))))),
      filter(([, data]) => {
        return isCustomer(data) && isInvestAccount(data);
      }),
      mergeMap(([, data]) =>
        this.yovaAPI.dashboard.getSavingsPlanSummary(data.id).pipe(
          map((response) => HomeActions.getSavingsPlanSummarySuccess({ data: response.data.attributes })),
          catchError(() => {
            HomeActions.getSavingsPlanSummaryFail({ data: null });
            return EMPTY;
          })
        )
      )
    )
  );

  getPerformanceSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.getPerformanceSummary),
      mergeMap(() =>
        this.yovaAPI.account.getPerformanceSummary().pipe(
          map((response) => HomeActions.getPerformanceSummarySuccess({ data: response.data.attributes })),
          catchError(() => of(HomeActions.getPerformanceSummaryFail()))
        )
      )
    )
  );

  getBalanceSheet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.getBalanceSheet),
      mergeMap(() =>
        this.yovaAPI.account.getBalanceSheet().pipe(
          map((response) => HomeActions.getBalanceSheetSuccess({ data: response.data.attributes })),
          catchError(() => of(HomeActions.getBalanceSheetFail()))
        )
      )
    )
  );

  getPromoCampaigns$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.getPromoCampaigns),
      mergeMap(() =>
        this.yovaAPI.dashboard.getPromoCampaigns().pipe(
          map((response) => HomeActions.getPromoCampaignsSuccess({ data: this.addPromosToCampaigns(response) })),
          catchError(() => of(HomeActions.getPromoCampaignsFail()))
        )
      )
    )
  );

  setCampaignParticipation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.setCampaignParticipation),
      concatMap((action) => of(action).pipe(withLatestFrom(this.store.pipe(select(fromHome.selectPromoCampaigns))))),
      mergeMap(([{ participation, id }, campaigns]) =>
        this.yovaAPI.dashboard.setPromoParticipation(participation, id).pipe(
          map(() =>
            HomeActions.setCampaignParticipationSuccess({
              data: campaigns.map((campaign) => {
                if (Number(campaign.id) === id) {
                  const updatedCampaign = cloneDeep(campaign);
                  updatedCampaign.attributes.opted_in = participation;
                  return updatedCampaign;
                }
                return campaign;
              })
            })
          ),
          catchError(() => {
            void this.toastService.error(this.translateService.instant('SHARED.errors.something'));
            return EMPTY;
          })
        )
      )
    )
  );

  getPerformanceMarketUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomeActions.getPerformanceMarketUpdate),
      concatLatestFrom(() => this.store.select(fromHome.selectPerformanceMarketUpdatesLoadState)),
      filter(([, loadState]) => loadState !== 'LOADED'),
      switchMap(() =>
        this.yovaAPI.dashboard.getPerformanceMarketUpdate().pipe(
          map((res) => HomeActions.getPerformanceMarketUpdateSuccess(res)),
          catchError((err) => of(HomeActions.getPerformanceMarketUpdateFail(err)))
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private toastService: ToastService,
    private store: Store,
    private router: Router,
    private translateService: TranslateService,
    private yovaAPI: YovaAPI,
    private storage: Storage,
    private amplitudeService: AmplitudeService
  ) {}

  private addPromosToCampaigns(response: PromoCampaign): PromoCampaign {
    const campaigns = response.data.map((campaign) => {
      const promoIDs = campaign.relationships.deposit_promotions.data.map((promo) => promo.id);
      return {
        ...campaign,
        promotions: response.included
          .filter((promotion) => {
            return promoIDs.includes(promotion.id);
          })
          .sort((a, b) => a.attributes.value - b.attributes.value)
      };
    });
    return { ...response, data: campaigns };
  }

  private getPerformanceFiltersStorageKey(accountId: string): string {
    return `${IonicStorageKeys.PERFORMANCE_FILTERS}_${accountId}`;
  }
}
